import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useUserSettings, useUserSettingsUpdate } from 'src/hooks';
import { ArchiveAll } from 'src/pages/ManageTasksChatPage/components/AccessAndDataSettings/components/ArchiveAll';
import { DisconnectAccess } from 'src/pages/ManageTasksChatPage/components/AccessAndDataSettings/components/DisconnectAccess';
import { DeleteAccount } from 'src/pages/ManageTasksChatPage/components/AccessAndDataSettings/components/DeleteAccount';
import { useCanConnectToGoogle } from 'src/hooks/useCanConnectToGoogle';
import { SettingKeys, SingleSelectSetting } from 'src/types';
import { SwitchSimple } from 'src/v2/commonComponents/SwitchSimple';
import { SelectSimple } from 'src/components/SelectSimple';
import { ManageAccountContentPage } from '../ManageAccountContentPage/ManageAccountContentPage';
import styles from './General.module.scss';

type TimeZoneOption = {
  value: string;
  label: string;
  time: string;
};

/**
 * @deprecated
 */
export const General = () => {
  const { setting: manuallySetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.MANUALLY_SELECTED_TIMEZONE,
  );

  const { setting: automaticallySetting } =
    useUserSettings<SingleSelectSetting>(
      SettingKeys.IS_TIMEZONE_SET_AUTOMATICALLY,
    );

  const { updateUserSetting } = useUserSettingsUpdate();

  const canConnectToGoogle = useCanConnectToGoogle();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore todo remove with "typescript" update - supportedValuesOf supported by TS version >= 5.1
  const listOfTimezones = Intl.supportedValuesOf('timeZone');

  const sortedListOfTimezones = useMemo(() => {
    return listOfTimezones
      .map((item: string) => ({
        label: `${item} ${new Date().toLocaleTimeString('en-US', {
          timeZone: item,
          hour12: true,
          timeStyle: 'short',
        })}`,
        value: item,
        time: new Date().toLocaleString('en-US', { timeZone: item }),
      }))
      .slice()
      .sort((a: TimeZoneOption, b: TimeZoneOption) =>
        dayjs(a.time).isAfter(dayjs(b.time)) ? 1 : -1,
      );
  }, [listOfTimezones]);

  const isAutomatically = !!automaticallySetting?.value;
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedTimezone = isAutomatically
    ? currentTimezone
    : manuallySetting?.value;

  const handleSwitchChange = async (checked: boolean) => {
    await updateUserSetting({
      settingKey: SettingKeys.IS_TIMEZONE_SET_AUTOMATICALLY,
      value: checked,
    });

    if (checked && manuallySetting?.value !== currentTimezone) {
      await updateUserSetting({
        settingKey: SettingKeys.MANUALLY_SELECTED_TIMEZONE,
        value: currentTimezone,
        hideToast: true,
      });
    }
  };

  const handleSelectChange = async (value: string) => {
    await updateUserSetting({
      settingKey: SettingKeys.MANUALLY_SELECTED_TIMEZONE,
      value,
    });
  };

  return (
    <ManageAccountContentPage
      title="General"
      subtitle="Manage general settings"
      isWithBackButton
    >
      <div className={styles.root}>
        <h4 className={styles.title}>Timezone</h4>

        <div className={styles.field}>
          <label className={styles.label}>Set timezone automatically</label>
          <SwitchSimple
            isChecked={isAutomatically}
            onChange={handleSwitchChange}
          />
        </div>

        <div className={`${styles.field} ${styles.select}`}>
          <SelectSimple
            aria-label="location-time"
            options={sortedListOfTimezones}
            className={styles.select}
            onChange={handleSelectChange}
            value={selectedTimezone}
            isDisabled={isAutomatically}
          />
        </div>

        <hr className={styles.divider} />

        <h4 className={styles.title}>Data control</h4>
        <h5 className={styles.subtitle}>Manage your data preferences.</h5>

        <ArchiveAll />

        {canConnectToGoogle && <DisconnectAccess />}

        <DeleteAccount />

        <hr className={styles.divider} />
      </div>
    </ManageAccountContentPage>
  );
};
